import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Shield } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { logGlobalError } from '../../utils/globalErrorLogger';

export function UserMenu() {
  const { currentUser, isAdmin, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onPointer);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onPointer);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const displayName = currentUser?.displayName || currentUser?.email || '';
  const photoURL = currentUser?.photoURL || null;
  const initial = getInitial(currentUser?.displayName, currentUser?.email);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-label="Open account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-sm font-semibold text-primary ring-1 ring-primary/20 hover:ring-primary/40 focus:outline-none focus:ring-2 focus:ring-primary"
      >
        {photoURL ? (
          <img
            src={photoURL}
            alt=""
            className="h-full w-full object-cover"
            referrerPolicy="no-referrer"
          />
        ) : (
          <span aria-hidden="true">{initial}</span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-56 rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        >
          <div className="border-b border-gray-100 px-3 py-2">
            <p className="truncate text-sm font-medium text-gray-900">{displayName}</p>
            {currentUser?.displayName && currentUser.email && (
              <p className="truncate text-xs text-gray-500">{currentUser.email}</p>
            )}
          </div>
          {isAdmin && (
            <Link
              to="/admin/users"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <Shield className="h-4 w-4" />
              Admin
            </Link>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              logout().catch((err) => {
                logGlobalError(err, { context: 'UserMenu.logout' });
              });
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function getInitial(
  displayName: string | null | undefined,
  email: string | null | undefined,
): string {
  const source = (displayName && displayName.trim()) || (email && email.trim()) || '';
  const first = source.charAt(0);
  return first ? first.toUpperCase() : '?';
}
